import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Check, Sparkle, Ticket, Trash, DownloadSimple, ArrowClockwise } from "@phosphor-icons/react";

const money = (v, c = "eur") => `${c === "eur" ? "€" : ""}${Number(v).toLocaleString()}`;

// Card payments are wired but go live only once the Stripe account is connected.
// Flip to true (and wire native subscriptions) to enable paid self-checkout.
const PAYMENTS_LIVE = true;

// Pre-launch: plan prices are hidden until pricing is finalised. Flip to true to
// show the Founder/Pro/Firm price cards again.
const SHOW_PRICING = false;

const ComingSoon = () => (
  <div className="rounded-xl bg-white/[0.04] border border-dashed border-white/15 p-3 text-center" data-testid="payments-coming-soon">
    <div className="text-xs font-semibold text-white/80">Card payments launching shortly</div>
    <div className="text-[11px] text-white/45 mt-0.5">On a programme? Use an access code below to unlock Pro now.</div>
  </div>
);

const FREE_FEATURES = ["1 company", "Setup Wizard & Compliance Calendar", "Tax-relief estimates", "A few receipts / 1 grant"];
const PRO_FEATURES = ["Unlimited receipts & grants", "Payroll & payslip PDFs", "Saved tax-relief calcs & exports", "Compliance certificates & Revenue exports", "Scheduled email reports"];
const FIRM_FEATURES = ["Everything in Pro", "Multiple companies", "Accountant portal & client management", "Priority support"];

export default function Billing() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [seats, setSeats] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [s, p] = await Promise.all([api.get("/billing/status"), api.get("/billing/plans")]);
    setStatus(s.data); setPlans(p.data.plans || []);
    try { const st = await api.get("/billing/seats"); setSeats(st.data); } catch { /* optional */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Return from Stripe: poll status
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const r = await api.get(`/billing/checkout/status/${sid}`);
        if (r.data.payment_status === "paid") { toast.success("Payment successful — welcome to Pro!"); load(); window.history.replaceState({}, "", "/billing"); return; }
        if (r.data.status === "expired" || tries >= 6) { toast.error("Payment not completed."); window.history.replaceState({}, "", "/billing"); return; }
      } catch (e) { console.debug("checkout status poll retry", e); }
      setTimeout(poll, 2000);
    };
    poll();
  }, [load]);

  const upgrade = async (planId) => {
    setBusy(true);
    try {
      const r = await api.post("/billing/checkout", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start checkout"); setBusy(false); }
  };

  const redeem = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/billing/redeem", { code });
      toast.success(`Code applied — Pro unlocked${r.data.label ? ` (${r.data.label})` : ""}!`);
      setCode(""); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Invalid code"); }
  };

  const proMonthly = plans.find((p) => p.id === "pro_monthly");
  const proAnnual = plans.find((p) => p.id === "pro_annual");
  const firm = plans.find((p) => p.id === "firm_monthly");
  const tier = status?.tier || "free";

  return (
    <div className="space-y-8">
      <div>
        <div className="eyebrow mb-2">Billing</div>
        <h1 className="font-display text-4xl">Plans & billing</h1>
        <p className="text-white/60 text-sm mt-2">{SHOW_PRICING ? "Priced against what it saves — avoided penalties, captured reliefs and accountant hours. No auto-charges — you\u2019re only billed when you choose to renew." : "PerCen360 is invite-only ahead of launch. Founding members have full access while we finalise pricing."}</p>
      </div>

      {/* Current plan + management */}
      <ManageSubscription status={status} busy={busy} onRenew={() => status?.plan_id && upgrade(status.plan_id)} />

      {seats && (seats.used > 0 || seats.extra > 0) && (
        <div className="glass rounded-xl p-6" data-testid="seat-usage-card">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="eyebrow mb-1">Team seats</div>
              <div className="font-display text-2xl">{seats.used} paid seat{seats.used === 1 ? "" : "s"} used</div>
              <div className="text-white/55 text-sm mt-1">
                {seats.included} included with your plan · {seats.extra} extra @ {money(seats.per_seat)}/period.
                Accountants are always free.
              </div>
              {seats.extra > 0 && seats.period_days === 30 && (
                <div data-testid="annual-upsell" className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-[#FFB020]/30 bg-[#FFB020]/10 px-3 py-2">
                  <span className="text-xs text-[#FFB020]">
                    💡 On annual, seats drop to {money(seats.annual_per_seat_monthly)}/mo — save {money(seats.extra * (seats.monthly_per_seat * 12 - seats.annual_per_seat))}/yr on your {seats.extra} extra seat{seats.extra === 1 ? "" : "s"}.
                  </span>
                  {PAYMENTS_LIVE && (tier === "pro" || tier === "firm") && (
                    <button data-testid="switch-annual-btn" disabled={busy}
                      onClick={() => upgrade(tier === "firm" ? "firm_annual" : "pro_annual")}
                      className="btn-gold text-xs py-1.5">Switch to annual</button>
                  )}
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-wider text-white/45">Next renewal</div>
              <div className="font-display text-3xl text-[#FFB020]" data-testid="projected-renewal">{money(seats.projected_renewal)}</div>
              <div className="text-[11px] text-white/40">{money(seats.base_amount)} plan{seats.extra_cost > 0 ? ` + ${money(seats.extra_cost)} seats` : ""}</div>
            </div>
          </div>
        </div>
      )}

      {status?.founding_member && <FoundingOffer status={status} onBuy={upgrade} busy={busy} />}

      {/* Plan cards */}
      {SHOW_PRICING ? (
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5">
        <PlanCard title="Founder" price="Free" period="" features={FREE_FEATURES} current={tier === "free"} />
        <PlanCard title="Pro" price={proMonthly ? money(proMonthly.amount) : "—"} period="/mo" highlight
          sub={proAnnual ? `or ${money(proAnnual.amount)}/yr (2 months free)` : ""}
          features={PRO_FEATURES} current={tier === "pro"}
          actions={PAYMENTS_LIVE ? (<>
            <button disabled={busy} onClick={() => upgrade("pro_monthly")} className="btn-gold w-full justify-center" data-testid="upgrade-pro_monthly">Get Pro — 30 days</button>
            <button disabled={busy} onClick={() => upgrade("pro_annual")} className="btn-ghost w-full justify-center mt-2" data-testid="upgrade-pro_annual">Get Pro — 1 year (save 2 months)</button>
          </>) : <ComingSoon />} />
        <PlanCard title="Firm" price={firm ? money(firm.amount) : "—"} period="/mo" features={FIRM_FEATURES} current={tier === "firm"}
          actions={PAYMENTS_LIVE
            ? <button disabled={busy} onClick={() => upgrade("firm_monthly")} className="btn-gold w-full justify-center" data-testid="upgrade-firm_monthly">Get Firm — 30 days</button>
            : <ComingSoon />} />
        <div className="glass rounded-2xl p-6 flex flex-col" data-testid="accelerator-card">
          <div className="flex items-center gap-2 mb-2"><Sparkle size={18} weight="fill" className="text-[#FFB020]" /><div className="font-display text-xl">Accelerator</div></div>
          <p className="text-white/55 text-sm flex-1">On a startup programme? Enter your access code to unlock Pro free for the duration — no card needed.</p>
          <form onSubmit={redeem} className="mt-4 space-y-2">
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="ACCESS CODE" data-testid="redeem-code-input" className="uppercase" />
            <button type="submit" className="btn-gold w-full justify-center" data-testid="redeem-code-btn"><Ticket size={15} weight="fill" /> Apply code</button>
          </form>
        </div>
      </div>
      ) : (
      <div className="grid md:grid-cols-2 gap-5" data-testid="pricing-hidden">
        <div className="glass rounded-2xl p-6 flex flex-col justify-center">
          <div className="flex items-center gap-2 mb-2"><Sparkle size={18} weight="fill" className="text-[#FFB020]" /><div className="font-display text-xl">Pricing announced at launch</div></div>
          <p className="text-white/55 text-sm">You have full access as a founding member while we finalise plans and pricing. We&apos;ll share the details well before your founding period ends — no card needed today.</p>
        </div>
        <div className="glass rounded-2xl p-6 flex flex-col" data-testid="accelerator-card">
          <div className="flex items-center gap-2 mb-2"><Sparkle size={18} weight="fill" className="text-[#FFB020]" /><div className="font-display text-xl">Have an access code?</div></div>
          <p className="text-white/55 text-sm flex-1">On a startup or accelerator programme? Enter your access code to unlock Pro — no card needed.</p>
          <form onSubmit={redeem} className="mt-4 space-y-2">
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="ACCESS CODE" data-testid="redeem-code-input" className="uppercase" />
            <button type="submit" className="btn-gold w-full justify-center" data-testid="redeem-code-btn"><Ticket size={15} weight="fill" /> Apply code</button>
          </form>
        </div>
      </div>
      )}

      {user?.is_admin && <CodeManager />}

      {tier !== "free" && <BillingHistory />}
      <BillingProfile />
    </div>
  );
}

function FoundingOffer({ status, onBuy, busy }) {
  const [offer, setOffer] = useState(null);
  useEffect(() => { api.get("/launch/founding-offer").then((r) => setOffer(r.data)).catch(() => {}); }, []);
  if (!offer || !offer.enabled) return null;
  const money = (n) => `€${Number(n || 0).toLocaleString()}`;
  return (
    <div className="rounded-2xl p-6" style={{ background: "linear-gradient(135deg,#FFB02018,#00A86B10)", border: "1px solid #FFB02033" }} data-testid="founding-offer">
      <div className="flex items-center gap-2 mb-1"><Sparkle size={18} weight="fill" className="text-[#FFB020]" /><div className="font-display text-xl">{offer.name} member pricing</div></div>
      <p className="text-white/60 text-sm mb-4">
        Your free founding period runs until <span className="mono text-white/80">{status?.valid_until?.slice(0, 10)}</span>
        {status?.days_left != null && ` (${status.days_left} days left)`}. Lock in your founding rate to continue without interruption.
      </p>
      <div className="flex flex-wrap gap-3">
        {offer.monthly && (
          <button disabled={busy} onClick={() => onBuy("founding_monthly")} data-testid="buy-founding-monthly" className="btn-gold">
            {money(offer.monthly)}/mo — continue founding
          </button>
        )}
        {offer.annual && (
          <button disabled={busy} onClick={() => onBuy("founding_annual")} data-testid="buy-founding-annual" className="btn-ghost">
            {money(offer.annual)}/yr — best value
          </button>
        )}
      </div>
    </div>
  );
}

function ManageSubscription({ status, onRenew, busy }) {
  const tier = status?.tier || "free";
  const isPaid = status?.source === "stripe";
  const isAccel = status?.source === "accelerator";
  const isFounding = status?.founding_member || status?.source === "founding";
  const endingSoon = (isPaid || isFounding) && status?.days_left != null && status.days_left <= 7;

  return (
    <div className="glass rounded-2xl p-6 flex flex-wrap items-center justify-between gap-4" data-testid="billing-status">
      <div>
        <div className="eyebrow mb-1">Current plan</div>
        <div className="font-display text-2xl capitalize flex items-center gap-2">
          {isFounding ? "Founding member" : (status?.label || "Free")}
          {isFounding && <Sparkle size={18} weight="fill" className="text-[#FFB020]" />}
        </div>
        {status?.valid_until && (
          <div className="text-white/50 text-sm mt-1">
            {isFounding ? "Founding access until " : "Access until "}{status.valid_until.slice(0, 10)}
            {status?.days_left != null && ` · ${status.days_left} days left`}
            {status?.source && !isFounding && ` · via ${status.source}`}
          </div>
        )}
        {endingSoon && !isFounding && <div className="text-[#FFB020] text-xs mt-1">Your access ends soon — renew to keep your Pro features.</div>}
        {isAccel && <div className="text-white/45 text-xs mt-1">Free via accelerator — lapses to Free when it ends (no card, nothing to cancel).</div>}
        {isFounding && <div className="text-white/55 text-xs mt-1" data-testid="founding-note">You&apos;re a founding member — enjoying full Pro access free during the founding period. Your founding pricing is being finalised; we&apos;ll be in touch before it ends.</div>}
      </div>
      <div className="flex items-center gap-3">
        {isFounding
          ? <span className="px-3 py-1 rounded-full bg-[#FFB020]/15 text-[#FFB020] text-xs font-semibold uppercase tracking-wider" data-testid="founding-badge">Founding</span>
          : tier !== "free" && <span className="px-3 py-1 rounded-full bg-[#00A86B]/15 text-[#00A86B] text-xs font-semibold uppercase tracking-wider">{tier} active</span>}
        {isPaid && <button disabled={busy} onClick={onRenew} className="btn-gold text-sm" data-testid="renew-subscription-btn"><ArrowClockwise size={15} /> Renew / extend</button>}
      </div>
    </div>
  );
}

function BillingHistory() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/billing/history").then((r) => setItems(r.data.items || [])).catch(() => {}); }, []);

  const download = async (sid) => {
    try {
      const res = await api.get(`/billing/invoice/${sid}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `invoice-${sid.slice(-8)}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download invoice"); }
  };

  return (
    <div className="glass rounded-2xl p-6" data-testid="billing-history">
      <div className="eyebrow mb-4">Billing history</div>
      {items.length === 0 ? <div className="text-white/40 text-sm">No payments yet. Invoices appear here after your first card payment.</div> : (
        <table className="data"><thead><tr><th>Date</th><th>Plan</th><th>Amount</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.session_id} data-testid={`invoice-${it.session_id}`}>
                <td className="mono">{it.date}</td>
                <td className="text-white/70">{it.label}</td>
                <td className="mono">€{Number(it.amount).toLocaleString()}</td>
                <td><span className="text-[#00A86B] text-xs uppercase">{it.status}</span></td>
                <td className="text-right">
                  <button onClick={() => download(it.session_id)} className="btn-ghost text-xs" data-testid={`download-invoice-${it.session_id}`}><DownloadSimple size={14} /> Invoice</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function BillingProfile() {
  const [f, setF] = useState({ billing_name: "", billing_address: "", vat_number: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/billing/profile").then((r) => setF({ billing_name: r.data.billing_name || "", billing_address: r.data.billing_address || "", vat_number: r.data.vat_number || "" })).catch(() => {}); }, []);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.put("/billing/profile", f); toast.success("Billing details saved — used on your invoices."); }
    catch { toast.error("Could not save billing details"); }
    finally { setSaving(false); }
  };
  return (
    <div className="glass rounded-2xl p-6" data-testid="billing-profile">
      <div className="eyebrow mb-1">Invoice details</div>
      <p className="text-white/50 text-sm mb-4">Shown on your downloadable invoices. Add your VAT number to reclaim VAT.</p>
      <form onSubmit={save} className="grid md:grid-cols-3 gap-3 items-start">
        <div><label>Billing name</label><input value={f.billing_name} onChange={(e) => setF({ ...f, billing_name: e.target.value })} data-testid="billing-name" placeholder="Company Ltd" /></div>
        <div><label>VAT number</label><input value={f.vat_number} onChange={(e) => setF({ ...f, vat_number: e.target.value })} data-testid="billing-vat" placeholder="IE1234567X" /></div>
        <div className="md:col-span-3"><label>Billing address</label><textarea value={f.billing_address} onChange={(e) => setF({ ...f, billing_address: e.target.value })} data-testid="billing-address" rows={3} placeholder="Street, City, Eircode, Country" /></div>
        <button type="submit" disabled={saving} className="btn-gold justify-center" data-testid="save-billing-profile">Save details</button>
      </form>
    </div>
  );
}

function PlanCard({ title, price, period, sub, features, actions, current, highlight }) {
  return (
    <div className={`glass rounded-2xl p-6 flex flex-col ${highlight ? "ring-1 ring-[#FFB020]/50" : ""}`} data-testid={`plan-${title.toLowerCase()}`}>
      <div className="font-display text-xl">{title}</div>
      <div className="mt-2"><span className="mono text-3xl">{price}</span><span className="text-white/40 text-sm">{period}</span></div>
      {sub ? <div className="text-xs text-white/45 mt-1">{sub}</div> : null}
      <ul className="space-y-2 my-5 flex-1">
        {features.map((f) => <li key={f} className="flex items-start gap-2 text-sm text-white/70"><Check size={15} weight="bold" className="text-[#00A86B] mt-0.5 shrink-0" /> {f}</li>)}
      </ul>
      {current ? <div className="text-center text-xs uppercase tracking-wider text-[#00A86B] py-2.5 border border-[#00A86B]/30 rounded-lg">Current plan</div> : actions}
    </div>
  );
}

function CodeManager() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ code: "", tier: "pro", duration_months: 12, max_redemptions: 0, label: "" });
  const load = useCallback(async () => {
    try {
      const r = await api.get("/billing/codes");
      setItems(r.data.items || []);
    } catch (e) {
      // /billing/codes is admin-only; non-admin owners get 403 — degrade quietly.
      console.debug("billing codes load skipped", e?.response?.status);
      setItems([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/billing/codes", { ...f, duration_months: Number(f.duration_months), max_redemptions: Number(f.max_redemptions) }); setF({ code: "", tier: "pro", duration_months: 12, max_redemptions: 0, label: "" }); load(); toast.success("Code created"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const del = async (c) => { await api.delete(`/billing/codes/${c}`); load(); };

  return (
    <div className="glass rounded-2xl p-6" data-testid="code-manager">
      <div className="eyebrow mb-4">Accelerator codes (admin)</div>
      <form onSubmit={create} className="grid md:grid-cols-5 gap-3 items-end mb-5">
        <div><label>Code</label><input required value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="new-code" className="uppercase" /></div>
        <div><label>Label</label><input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} data-testid="new-code-label" placeholder="Cohort name" /></div>
        <div><label>Months</label><input type="number" value={f.duration_months} onChange={(e) => setF({ ...f, duration_months: e.target.value })} data-testid="new-code-months" /></div>
        <div><label>Max uses (0=∞)</label><input type="number" value={f.max_redemptions} onChange={(e) => setF({ ...f, max_redemptions: e.target.value })} data-testid="new-code-max" /></div>
        <button type="submit" className="btn-gold justify-center" data-testid="create-code-btn">Create</button>
      </form>
      {items.length === 0 ? <div className="text-white/40 text-sm">No codes yet.</div> : (
        <table className="data"><thead><tr><th>Code</th><th>Label</th><th>Tier</th><th>Months</th><th>Used</th><th></th></tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.code} data-testid={`code-${c.code}`}>
                <td className="mono">{c.code}</td><td className="text-white/70">{c.label}</td><td>{c.tier}</td><td>{c.duration_months}</td>
                <td className="mono">{c.redemptions}{c.max_redemptions ? `/${c.max_redemptions}` : ""}</td>
                <td className="text-right"><button onClick={() => del(c.code)} className="text-white/40 hover:text-[#E63946]" data-testid={`del-code-${c.code}`}><Trash size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
